import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { TimeReportForStamping } from "model/Company";
import { useCallback } from "react";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { AutoCloseCountdown } from "components/designV2/autoCloseCountdown/autoCloseCountdown";
import styled from "styled-components";
import {
  formatMinutesDuration,
  formatTimeLabel,
  formatTimeRangeLabel,
} from "web-apps/company/utils/timeAndDate";
import type { StampResult } from "./clockStamp.logic";
import type { ClockStampSubmitData } from "./clockStampTimeReportPanel.logic";

type Props = {
  timeReport?: TimeReportForStamping;
  stampResult: StampResult | null;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  isAutoGeneratedGig: boolean;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  handleEditTimeReport: () => void;
  handleSubmitDefaultTimeReport: () => void;
  timeReportDefaultSubmitData: ClockStampSubmitData | null;
  hasTimeReport: boolean;
  isTimeReportLoading: boolean;
  isSubmittingTimeReport: boolean;
  clearInputs: () => void;
};

export const ClockStampCheckInOutModal: React.FC<Props> = ({
  timeReport,
  stampResult,
  setShowModal,
  setKeyShouldBeDisabled,
  showModal,
  isAutoGeneratedGig,
  setIsAutoGeneratedGig,
  handleEditTimeReport,
  handleSubmitDefaultTimeReport,
  timeReportDefaultSubmitData,
  hasTimeReport,
  isTimeReportLoading,
  isSubmittingTimeReport,
  clearInputs,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const isCheckOut =
    timeReport?.checkInTime && !isAutoGeneratedGig && !timeReport?.checkOutTime;
  const userName = (timeReport?.firstName || "").trim();
  const greeting = isCheckOut
    ? t("StampClock.HaveANiceDay")
    : t("StampClock.Welcome");
  const greetingText = userName ? `${greeting} ${userName}!` : `${greeting}!`;

  const checkInTime = stampResult?.checkInTime ?? timeReport?.checkInTime;

  const breakMinutes = Number(timeReportDefaultSubmitData?.breakTime) || 0;
  const breakLabel =
    breakMinutes > 0 &&
    timeReportDefaultSubmitData?.breakStartTime &&
    timeReportDefaultSubmitData?.breakEndTime
      ? t("TimeReports.BreakWithRange", {
          duration: formatMinutesDuration(breakMinutes),
          range: formatTimeRangeLabel(
            timeReportDefaultSubmitData.breakStartTime,
            timeReportDefaultSubmitData.breakEndTime,
            locale,
          ),
        })
      : formatMinutesDuration(breakMinutes);
  const isBusy = isTimeReportLoading || isSubmittingTimeReport;
  // Editing stays available when the defaults cannot be submitted as they are,
  // since that is the case where the report has to be adjusted by hand.
  const isEditDisabled = isBusy || !hasTimeReport;
  const isSubmitDisabled = isBusy || !timeReportDefaultSubmitData;

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setKeyShouldBeDisabled(false);
    setIsAutoGeneratedGig(false);
    clearInputs();
  }, [
    setShowModal,
    setKeyShouldBeDisabled,
    setIsAutoGeneratedGig,
    clearInputs,
  ]);

  return (
    <Modal
      label=""
      size="compact"
      isOpen={showModal}
      onClose={handleCloseModal}
    >
      <ModalContent>
        <Text typography="h3" align="center" color={ColorV2.Blue1}>
          {greetingText}
        </Text>
        {isCheckOut ? (
          <>
            {isTimeReportLoading ? (
              <SummaryLoader>
                <CircularProgress size={28} />
              </SummaryLoader>
            ) : (
              timeReportDefaultSubmitData && (
                <SummaryRows>
                  <SummaryRow>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {t("StampClock.WorkingHours")}
                    </Text>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {formatTimeRangeLabel(
                        timeReportDefaultSubmitData.startTime,
                        timeReportDefaultSubmitData.endTime,
                        locale,
                      )}
                    </Text>
                  </SummaryRow>
                  <SummaryRow>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {t("TimeReports.Break")}
                    </Text>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {breakLabel}
                    </Text>
                  </SummaryRow>
                </SummaryRows>
              )
            )}
            <Text typography="b1" align="center" color={ColorV2.Blue1}>
              {t("StampClock.SubmitTimeReportPrompt")}
            </Text>
            <Actions>
              <SecondaryButton
                label={t("StampClock.Edit")}
                onClick={handleEditTimeReport}
                disabled={isEditDisabled}
                size="small"
              />
              <PrimaryButton
                label={t("StampClock.Submit")}
                onClick={handleSubmitDefaultTimeReport}
                disabled={isSubmitDisabled}
                isLoading={isSubmittingTimeReport}
                size="small"
              />
            </Actions>
          </>
        ) : (
          <>
            <Text typography="b1" align="center" color={ColorV2.Blue1}>
              {t("StampClock.YouAreCheckedInAt", {
                time: formatTimeLabel(checkInTime, locale),
              })}
            </Text>
            {timeReport && (
              <ShiftInfo>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {t("StampClock.ShiftToday")}
                </Text>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {formatTimeRangeLabel(
                    timeReport.startTime,
                    timeReport.endTime,
                    locale,
                  )}
                </Text>
              </ShiftInfo>
            )}
            <AutoCloseCountdown onComplete={handleCloseModal} />
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SummaryLoader = styled.div`
  display: flex;
  justify-content: center;
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
`;

const ShiftInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
  margin-top: 14px;
`;
